import { isEmailValidator } from '@currency-one/validators'
import { pushDataLayer } from 'utils/data-layer-push'
import { signUpForNewsletter, NewsletterResponse } from 'services/newsletter-service'
import { t9n } from 'src/assets/scripts/t9n/t9n'

enum ErrorType {
  EmptyField = 'emptyField',
  EmailNotValid = 'emailNotValid',
  RequiredField = 'requiredField',
  EmailReserved = 'emailReserved',
  ServerUnknownError = 'serverUnknownError',
}

enum ErrorPlace {
  Email = 'email',
  Rodo = 'rodo',
  Server = 'server',
}

const $emailElement = document.querySelector<HTMLInputElement>('[data-newsletter-email]')
const $rodoElement = document.querySelector<HTMLInputElement>('[data-newsletter-rodo]')
const $subModal = document.querySelector<HTMLElement>('[data-target="newsletter-popup"]')
const $succModal = document.querySelector<HTMLElement>('[data-target="newsletter-popup-success"]')
const $closeModalButton = document.querySelector<HTMLElement>('[action-hide-newsletter-modal]')
const $closeSuccessButtons = document.querySelectorAll<HTMLButtonElement>('[action-hide-newsletter-success-modal]')
const $openModalButton = document.querySelector<HTMLButtonElement>('[action-show-newsletter-modal]')
const $newsletterSubmitButton = document.querySelector<HTMLButtonElement>('[data-submit-newsletter]')

const removeErrorMessages = (): void => {
  document.querySelectorAll<HTMLElement>('[data-error-field]').forEach(($error) => ($error.hidden = true))
}

const showError = (errorType: ErrorType, errorPlace: ErrorPlace): void => {
  const $errorElement = document.querySelector<HTMLElement>(`[data-error-field="${errorPlace}"]`)
  if ($errorElement) {
    $errorElement.hidden = false
    $errorElement.textContent = t9n(`formValidation.${errorType}`)
  }
}

const validateForm = (): { email: string; rodo: boolean } | null => {
  const $email = $emailElement ? $emailElement.value : ''
  if ($email.length <= 0) return showError(ErrorType.EmptyField, ErrorPlace.Email), null
  if (!isEmailValidator($email)) return showError(ErrorType.EmailNotValid, ErrorPlace.Email), null

  const $rodo = $rodoElement ? $rodoElement.checked : false
  if (!$rodo) return showError(ErrorType.RequiredField, ErrorPlace.Rodo), null

  return { email: $email, rodo: $rodo }
}

const handleApiResponse = (response): void => {
  if (response === 'EMAIL_ADDED') {
    if ($subModal) $subModal.hidden = true
    if ($succModal) $succModal.hidden = false

    pushDataLayer('form_submitted', { form_name: 'Zapis do Newslettera' })
  } else {
    console.error('Unexpected response:', response)
    showError(ErrorType.ServerUnknownError, ErrorPlace.Server)
  }
}

const handleApiError = (error: NewsletterResponse): void => {
  switch (error.response) {
    case 'ALREADY_SUBSCRIBED':
      showError(ErrorType.EmailReserved, ErrorPlace.Email)
      break
    case 'INVALID_EMAIL':
      showError(ErrorType.EmailNotValid, ErrorPlace.Email)
      break
    case 'INVALID_LIST':
      showError(ErrorType.ServerUnknownError, ErrorPlace.Server)
      break
    default:
      console.error('Unexpected error:', error)
  }
}

const submitNewsletterForm = (): void => {
  const formData = validateForm()
  if (!formData) return

  signUpForNewsletter({ email: formData.email }).then(handleApiResponse).catch(handleApiError)
}

const bindSingleAction = ($element: HTMLElement | null, event: string, callback: () => void): void => {
  if ($element) {
    $element.addEventListener(event, callback, false)
  }
}

const bindMultipleActions = ($elements, event: string, callback: () => void): void => {
  $elements.forEach(($element) => $element.addEventListener(event, callback, false))
}

const bindModalActions = (): void => {
  bindSingleAction($closeModalButton, 'click', () => {
    removeErrorMessages()
    if ($subModal) $subModal.hidden = true

    pushDataLayer('click_button', {
      button_type: 'Button',
      button_text: 'Zamknięcie popupu newslettera',
    })
  })

  bindMultipleActions($closeSuccessButtons, 'click', () => {
    if ($succModal) $succModal.hidden = true
  })

  bindSingleAction($openModalButton, 'click', () => {
    if ($subModal) $subModal.hidden = false
  })
}

const bindNewsletterFormActions = (): void => {
  bindSingleAction($newsletterSubmitButton, 'click', () => {
    removeErrorMessages()
    submitNewsletterForm()
  })
}

// Initializes the newsletter popup by binding event listeners for modal visibility
// and form submission (validating input and handling API responses).
const initNewsletterPopup = (): void => {
  // Bind actions related to modal visibility
  bindModalActions()

  // Bind actions related to the newsletter form
  bindNewsletterFormActions()
}

export default initNewsletterPopup
